import React, { useState, useEffect, useCallback } from 'react';
import type { Stats, TabId } from '../../types';
import { useLang, type Lang } from '../../i18n/context';

/** Dub console channel strip button with animated VU + fader */
function SupportButton({ isOn, onClick, label }: { isOn: boolean; onClick: () => void; label: string }) {
  // VU bar heights — on (peaked) vs off (idle but visible)
  const vuOff  = [3, 5, 7, 10, 12, 10, 7, 10, 12, 10, 7, 5, 3];
  const vuOn   = [6, 10, 8, 13, 10, 16, 12, 14, 9, 12, 7, 9, 5];
  const vu = isOn ? vuOn : vuOff;
  // Fader position
  const faderLeft = isOn ? '75%' : '35%';

  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 16px 8px',
        background: isOn
          ? 'linear-gradient(180deg, #3a3a52 0%, #2a2a3e 12%, #1e1e32 50%, #14142a 85%, #0a0a14 100%)'
          : 'linear-gradient(180deg, #2a2a3a 0%, #1a1a2e 50%, #0a0a14 100%)',
        border: `1.5px solid ${isOn ? '#c9a84c' : '#666'}`,
        borderRadius: '5px',
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px',
        boxShadow: isOn
          ? '0 2px 12px rgba(201,168,76,0.35), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -1px 0 rgba(0,0,0,0.3)'
          : '0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
        transition: 'all 0.4s ease',
      }}
      onMouseEnter={e => {
        if (!isOn) {
          (e.currentTarget as HTMLElement).style.borderColor = '#777';
        }
      }}
      onMouseLeave={e => {
        if (!isOn) {
          (e.currentTarget as HTMLElement).style.borderColor = '#444';
        }
      }}
    >
      {/* VU meter — 13 bars */}
      <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '16px' }}>
        {vu.map((h, i) => {
          const isHot = i >= 4 && i <= 8;
          const isPeak = i === 5;
          return (
            <div key={i} style={{
              width: '3px',
              height: `${h}px`,
              borderRadius: '0.5px',
              background: isOn
                ? (isPeak ? '#c9a84c' : isHot ? '#8a7535' : '#3a3a3a')
                : (isPeak ? '#8a7535' : isHot ? '#5a5030' : '#3a3a3a'),
              boxShadow: isOn && isPeak ? '0 0 6px rgba(201,168,76,0.6)' : isOn && isHot ? '0 0 3px rgba(201,168,76,0.3)' : 'none',
              transition: 'height 0.4s ease, background 0.4s, box-shadow 0.4s',
            }} />
          );
        })}
      </div>
      {/* Label */}
      <span style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '8px', fontWeight: 700,
        color: isOn ? '#c9a84c' : '#9a8a5a', letterSpacing: '1.5px',
        transition: 'color 0.4s',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}>{label}</span>
      {/* Fader track */}
      <div style={{
        width: '48px', height: '3px', borderRadius: '1.5px',
        background: 'linear-gradient(90deg, #2a2a2a 0%, #444 50%, #2a2a2a 100%)',
        position: 'relative',
        marginTop: '1px',
      }}>
        {/* Fader knob */}
        <div style={{
          position: 'absolute', top: '-2px',
          left: faderLeft, transform: 'translateX(-50%)',
          width: '10px', height: '7px', borderRadius: '1.5px',
          background: 'linear-gradient(180deg, #aaa 0%, #666 50%, #444 100%)',
          border: '0.5px solid #888',
          boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
          transition: 'left 0.4s ease-out',
        }} />
        {/* Track marks */}
        {[20, 35, 50, 65, 80].map(pct => (
          <div key={pct} style={{
            position: 'absolute', top: '-1px',
            left: `${pct}%`, width: '0.5px', height: '5px',
            background: '#333',
          }} />
        ))}
      </div>
    </button>
  );
}
import { useAuth } from '../../contexts/AuthContext';

interface HeaderProps {
  stats: Stats | null;
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
}

const LANGS: Array<{ id: Lang; label: string }> = [
  { id: 'fr', label: 'FR' },
  { id: 'en', label: 'EN' },
  { id: 'nl', label: 'NL' },
];

export function Header({ stats, activeTab, onTabChange }: HeaderProps) {
  const { t, lang, setLang } = useLang();
  const { isAdmin, login, logout } = useAuth();
  const [dateTime, setDateTime] = useState(new Date());
  const [showLogin, setShowLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const interval = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (d: Date) => {
    const days = t('date.days').split(',');
    const months = t('date.months').split(',');
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${lang === 'fr' ? 'à' : lang === 'nl' ? 'om' : 'at'} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="app-header">
      <div className="header-top">
        <div className="header-brand">
          <svg className="header-logo" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="48" fill="#1a1a2e" stroke="#c9a84c" strokeWidth="3"/>
            <circle cx="50" cy="50" r="35" fill="none" stroke="#c9a84c" strokeWidth="2" opacity="0.6"/>
            <circle cx="50" cy="50" r="22" fill="none" stroke="#c9a84c" strokeWidth="1.5" opacity="0.4"/>
            <circle cx="50" cy="50" r="10" fill="#c9a84c"/>
            <circle cx="50" cy="50" r="4" fill="#1a1a2e"/>
          </svg>
          <div>
            <div className="header-title">RIDDIM RADAR</div>
            <div className="header-subtitle">{t('header.subtitle')}</div>
          </div>
        </div>
        {/* Tagline — fills the empty space between brand and status, dub/rasta scripture vibe */}
        <div className="header-tagline">
          Calling the meek and the humble…
        </div>
        <div className="header-status">
          <div className="veille-badge">
            <span className="veille-dot"></span>
            {t('header.active')}
          </div>
          <div>
            <div className="header-datetime">{formatDate(dateTime)}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
              {stats && <span className="header-check">&#10003; OK</span>}
              <div style={{ display: 'flex', gap: '2px', marginLeft: '8px' }}>
                {LANGS.map(l => (
                  <button key={l.id} onClick={() => setLang(l.id)} style={{
                    padding: '1px 5px', fontSize: '9px', fontWeight: 700,
                    letterSpacing: '0.5px', cursor: 'pointer',
                    background: lang === l.id ? 'rgba(201,168,76,0.15)' : 'transparent',
                    border: `1px solid ${lang === l.id ? '#c9a84c' : '#333'}`,
                    borderRadius: '3px',
                    color: lang === l.id ? '#c9a84c' : '#666',
                    transition: 'all 0.2s',
                  }}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
              {isAdmin ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{
                    fontSize: '8px', fontWeight: 700, padding: '1px 5px',
                    background: 'rgba(76,175,80,0.15)', border: '1px solid #4caf50',
                    borderRadius: '3px', color: '#4caf50', letterSpacing: '0.5px',
                  }}>ADMIN</span>
                  {/* IG challenge indicator — only visible to admins (they can act on it).
                      Fires when: session expired OR any handle was challenged in last 24h.
                      Tooltip lists the challenged handles. */}
                  {stats && (!stats.ig_session_ok || (stats.ig_challenges_24h ?? 0) > 0) && (
                    <span
                      title={
                        !stats.ig_session_ok
                          ? 'IG session expired — manual re-login needed'
                          : `IG challenges in last 24h: ${stats.ig_challenges_24h}` +
                            (stats.ig_challenge_handles?.length
                              ? ` (${stats.ig_challenge_handles.slice(0, 6).join(', ')}${stats.ig_challenge_handles.length > 6 ? '…' : ''})`
                              : '')
                      }
                      style={{
                        fontSize: '8px', fontWeight: 700, padding: '1px 5px',
                        background: 'rgba(239,83,80,0.15)', border: '1px solid #ef5350',
                        borderRadius: '3px', color: '#ef5350', letterSpacing: '0.5px',
                        cursor: 'help',
                      }}
                    >
                      IG&nbsp;⚠{(stats.ig_challenges_24h ?? 0) > 0 ? ` ${stats.ig_challenges_24h}` : ''}
                    </span>
                  )}
                  <button onClick={logout} style={{
                    padding: '1px 5px', fontSize: '9px', cursor: 'pointer',
                    background: 'transparent', border: '1px solid #333',
                    borderRadius: '3px', color: '#666',
                  }} title={t('auth.logout')}>&#x2715;</button>
                </div>
              ) : (
                <button onClick={() => setShowLogin(true)} style={{
                  padding: '1px 5px', fontSize: '9px', cursor: 'pointer',
                  background: 'transparent', border: '1px solid #333',
                  borderRadius: '3px', color: '#666',
                }} title={t('auth.login')}>
                  <span style={{ position: 'relative' }}>
                    &#x1F512;
                    {/* Dot fires on session-expired OR any recent challenge so an admin
                        seeing it logged-out is prompted to log in and check. */}
                    {stats && (!stats.ig_session_ok || (stats.ig_challenges_24h ?? 0) > 0) && (
                      <span style={{
                        position: 'absolute', top: '-4px', right: '-6px',
                        width: '7px', height: '7px', borderRadius: '50%',
                        background: '#ef5350',
                        boxShadow: '0 0 4px rgba(239,83,80,0.6)',
                      }} />
                    )}
                  </span>
                </button>
              )}
            </div>
            {/* Support — dub mixing console channel strip, own row */}
            <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'flex-end' }}>
              <SupportButton
                isOn={activeTab === 'support'}
                onClick={() => onTabChange?.(activeTab === 'support' ? 'agenda' : 'support')}
                label={t('support.button_short')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Login modal */}
      {showLogin && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.7)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }} onClick={() => { setShowLogin(false); setLoginError(''); setPassword(''); }}>
          <div style={{
            background: '#1a1a2e', border: '1px solid #c9a84c',
            borderRadius: '8px', padding: '24px', minWidth: '280px',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ color: '#c9a84c', fontWeight: 700, fontSize: '14px', marginBottom: '16px', letterSpacing: '1px' }}>
              {t('auth.login')}
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoginError('');
              const result = await login(password);
              if (result.ok) {
                setShowLogin(false);
                setPassword('');
              } else {
                setLoginError(result.error || t('auth.invalid_password'));
              }
            }}>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={t('auth.password')}
                autoFocus
                style={{
                  width: '100%', padding: '8px 12px', fontSize: '13px',
                  background: '#0d0d1a', border: '1px solid #333',
                  borderRadius: '4px', color: '#eee', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              {loginError && (
                <div style={{ color: '#ef5350', fontSize: '11px', marginTop: '6px' }}>
                  {loginError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setShowLogin(false); setLoginError(''); setPassword(''); }}
                  style={{
                    padding: '6px 14px', fontSize: '11px', cursor: 'pointer',
                    background: 'transparent', border: '1px solid #333',
                    borderRadius: '4px', color: '#999',
                  }}>{t('common.cancel')}</button>
                <button type="submit" style={{
                  padding: '6px 14px', fontSize: '11px', cursor: 'pointer',
                  background: 'rgba(201,168,76,0.15)', border: '1px solid #c9a84c',
                  borderRadius: '4px', color: '#c9a84c', fontWeight: 700,
                }}>OK</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
